import Download from "@mui/icons-material/Download";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CreditProgress from "../../components/CreditProgress";
import ExpiredOverlay from "../../components/ExpiredOverlay";
import PricingModal from "../../components/PricingModal";
import Section from "../../components/Section";
import UpgradeBanner from "../../components/UpgradeBanner";
import UploadDropzone from "../../components/UploadDropzone";
import analytics from "../../util/analytics";
import { useAuth } from "$/util/auth";
import {
  createJob,
  getImageUrl,
  refreshUser,
  subscribeJob,
  uploadUserFile,
} from "$/util/db";
import supabase from "../../util/supabase";
import commonConstants from "$/data/common";

const CreateStencilTool = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  const [imageUrl, setImageUrl] = useState(null);
  const [job, setJob] = useState<{
    id?: string;
    status: string;
    result?: any;
  }>(null);
  const [userFiles, setUserFiles] = useState([]);
  const [shouldCreate, setShouldCreate] = useState(false);

  const creditsDepleted = auth.user && auth.user.credits <= 0 && !auth.isProUser;
  const anonCreditsDepleted =
    auth.isAnonymousUser && auth.user && auth.user.credits <= 4;

  const onRemix = (idx) => {
    navigate("/", { state: { creationId: job.id, variant: idx } });
  };

  const handleError = (err) => {
    Sentry.captureException(err);
    setJob({ status: "ERROR" });
    supabase.removeAllChannels();
  };

  const handleJobEvent = (jobEvent) => {
    setJob(jobEvent.new);
    if (jobEvent.new.status === "SUCCESS") {
      if (auth.user) {
        refreshUser(auth.user.id);
      }
      supabase.removeAllChannels();
    } else if (jobEvent.new.status === "ERROR") {
      handleError(jobEvent.new.error);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!acceptedFiles.length) {
      return;
    }

    if (!auth.user) {
      // Join anonymously, passthrough user because hooks won't have updated user yet
      await auth.signinAnonymously();
    }

    setUserFiles(acceptedFiles);
    setShouldCreate(true);
    setJob({ status: "INITIAL" });
  };

  const runCreate = async () => {
    if (!auth.user) {
      throw new Error("User not authenticated");
    }

    const file = userFiles[0];
    const url = URL.createObjectURL(file);
    setImageUrl(url);

    // Upload image to bucket
    analytics.track("upload_image", {
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
    });

    const resp = await uploadUserFile(auth.user.id, file);
    const { data, error } = resp;
    if (error) {
      Sentry.captureException(error);
      return;
    }

    const { path: uploadPath } = data;

    // Process image
    try {
      const response = await createJob(
        auth.user.id,
        {
          upload_path: uploadPath,
        },
        "upload",
      );
      const upscaleJob = response[0];

      subscribeJob(upscaleJob.id, handleJobEvent);
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    if (shouldCreate && auth.user) {
      runCreate();
      setShouldCreate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreate, auth.user]);

  if (job && job.status === "SUCCESS") {
    const bwImageUrl = getImageUrl(job.result.edge_image_url, "jobs");
    // const colorImageUrl = getImageUrl(job.result.image_url, "jobs");
    return (
      <>
        <Section>
          <Container maxWidth="md">
            <Box textAlign="center">
              <Typography variant="h2" color="inherit" mb={2}>
                Successfully Converted to Stencil!
              </Typography>
            </Box>
            {/* <Grid item xs={12} md={6}>
                <Box textAlign="center" mb={6}>
                  <Typography variant="h4" color="inherit">
                    Full Color
                  </Typography>
                  <Typography variant="body1" color="inherit">
                    Best for preserving color of the original design and sticks closer
                    to the original imagery.
                  </Typography>
                  <img src={colorImageUrl} alt="Uploaded Tattoo" height="300" />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onRemix(0)}
                    size="large"
                    fullWidth
                    startIcon={<ModelTrainingIcon />}
                  >
                    Remix
                  </Button>
                </Box>
              </Grid> */}
            <Box textAlign="center" mb={6}>
              <Typography variant="body1" color="inherit">
                From here you can dowload or remix your design into different
                variations. Works well with stencil, minimalist, and sketch styles.
              </Typography>
              <img
                src={bwImageUrl}
                alt="Uploaded Tattoo"
                width="100%"
                style={{ maxWidth: 512 }}
              />
              <Stack direction="row" spacing={2} mt={4}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => onRemix(1)}
                  size="large"
                  fullWidth
                  startIcon={<ModelTrainingIcon />}
                >
                  Remix
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    // Download image
                    const a = document.createElement("a");
                    a.href = `${bwImageUrl}?download=stencil.webp`;
                    a.click();
                  }}
                  size="large"
                  fullWidth
                  startIcon={<Download />}
                >
                  Download
                </Button>
              </Stack>
            </Box>
          </Container>
        </Section>
      </>
    );
  }

  return (
    <>
      <Box>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            {!auth.isProUser && (
              <CreditProgress credits={auth.user ? auth.user.credits : commonConstants.FREE_CREDITS} />
            )}

            <Box my={{ sm: 0, md: 1 }} sx={{ position: "relative" }}>
              {imageUrl ? (
                <Box textAlign="center">
                  <Typography variant="h2" color="inherit" mb={4}>
                    Converting to Stencil...
                  </Typography>
                  <Box mb={4}>
                    <CircularProgress size={64} />
                  </Box>
                  <img src={imageUrl} alt="Uploaded Tattoo" height="300" />
                </Box>
              ) : (
                <>
                  {creditsDepleted && <PricingModal />}
                  {(creditsDepleted || auth.isAnonymousUser) && <ExpiredOverlay />}
                  <UploadDropzone
                    onDrop={onDrop}
                    disabled={creditsDepleted || anonCreditsDepleted}
                  />
                  <Typography
                    variant="h5"
                    color="inherit"
                    sx={{ mt: 4, textAlign: "center" }}
                  >
                    Upload Recommendations
                  </Typography>
                  <ul>
                    <li>Must be 512x512px resolution or higher</li>
                    <li>Must be PNG or JPG file</li>
                    <li>Square image recommended (will be cropped)</li>
                    <li>
                      Ideal images have clear subject matter in the center of the image,
                      with a lot of contrast from the background
                    </li>
                  </ul>
                </>
              )}
            </Box>

            <Box my={3} sx={{ display: { xs: "none", md: "block" } }}>
              <UpgradeBanner context="upload" />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h2" color="inherit">
              Image-to-Stencil
            </Typography>
            <Typography variant="h5" color="inherit" sx={{ my: 2 }}>
              Upload your own tattoo design and convert it to a stencil
            </Typography>
            <img src="/images/tattooify.webp" alt="Tattooify Example" width="100%" />

            <Typography
              variant="h5"
              color="inherit"
              sx={{ mt: 4, textAlign: "center" }}
            >
              How It Works
            </Typography>
            <ol>
              <li>
                Your initial upload is kept private. Remixing the images will be public
                unless you are a Pro customer.
              </li>
              <li>We crop the image around the center to a square.</li>
              <li>We remove the background from the image.</li>
              <li>We convert the image to a stencil style.</li>
              <li>Download or remix and get 100s of variations of your design!</li>
            </ol>
          </Grid>
        </Grid>

        <Box my={3} sx={{ display: { xs: "block", md: "none" } }}>
          <UpgradeBanner context="upload" />
        </Box>
      </Box>
    </>
  );
};

export default CreateStencilTool;
